/**
 * Environment variable normalizer for different React frameworks
 * Supports Next.js, Vite, Create React App, and generic setups
 */
/**
 * Get client-side environment variable across different frameworks
 * @param key - The environment variable key (without framework prefix)
 * @returns The environment variable value or undefined
 *
 * @example
 * // For OPENAI_API_KEY, will check:
 * // - NEXT_PUBLIC_OPENAI_API_KEY (Next.js)
 * // - VITE_OPENAI_API_KEY (Vite)
 * // - REACT_APP_OPENAI_API_KEY (Create React App)
 * // - OPENAI_API_KEY (fallback)
 * // - window.env.OPENAI_API_KEY (runtime config)
 * const apiKey = getClientSideEnvVar('OPENAI_API_KEY');
 */
export function getClientSideEnvVar(key) {
    var _a, _b;
    // Next.js convention: NEXT_PUBLIC_*
    if (process.env["NEXT_PUBLIC_".concat(key)]) {
        return process.env["NEXT_PUBLIC_".concat(key)];
    }
    // Vite convention: VITE_*
    if (typeof import.meta !== "undefined" && ((_a = import.meta.env) === null || _a === void 0 ? void 0 : _a["VITE_".concat(key)])) {
        return import.meta.env["VITE_".concat(key)];
    }
    // Create React App convention: REACT_APP_*
    if (process.env["REACT_APP_".concat(key)]) {
        return process.env["REACT_APP_".concat(key)];
    }
    // Fallback: check if variable exists without prefix
    if (process.env[key]) {
        return process.env[key];
    }
    // Check window object for runtime config (common in some setups)
    if (typeof window !== "undefined" && ((_b = window.env) === null || _b === void 0 ? void 0 : _b[key])) {
        return window.env[key];
    }
    return undefined;
}
/**
 * Get multiple environment variables at once
 * @param keys - Array of environment variable keys
 * @returns Object mapping keys to their values
 *
 * @example
 * const { OPENAI_API_KEY, API_URL } = getEnvVars(['OPENAI_API_KEY', 'API_URL']);
 */
export function getEnvVars(keys) {
    return keys.reduce(function (acc, key) {
        acc[key] = getClientSideEnvVar(key);
        return acc;
    }, {});
}
/**
 * Check if an environment variable exists
 * @param key - The environment variable key
 * @returns true if the variable exists and is not empty
 */
export function hasEnvVar(key) {
    var value = getClientSideEnvVar(key);
    return value !== undefined && value !== "";
}
